import { Button } from "@devhop/ui";
import { CameraIcon, GripVerticalIcon, Trash2Icon } from "lucide-react";
import { useCallback, useState } from "react";
import { AppImage } from "@/shared/ui/AppImage";
import { PresignedImageUploadTrigger } from "@/shared/ui/PresignedImageUploadTrigger";
import type { ShopSkuMediaItem } from "../api/client";
import {
  useAddShopSkuMedia,
  useDeleteShopSkuMedia,
  useReorderShopSkuMedia,
  useShopSkuMediaQuery,
} from "../api/queries";

const KEY_PREFIX = "uploads/catalog/skus";
const MAX_SKU_IMAGES = 8;

type SkuAngleKind = "front" | "back" | "side" | "detail" | "package";

/** ຕົວຢ່າງຮູບຖ່າຍສິນຄ້າ (ອະໄຫຼ່) ແຕ່ລະມຸມ — flat illustration ເປັນໄກ້ດ໌ */
const SKU_PHOTO_GUIDE: { kind: SkuAngleKind; label: string; hint: string }[] = [
  { kind: "front", label: "ດ້ານໜ້າ", hint: "ຮູບຫຼັກ (thumbnail)" },
  { kind: "back", label: "ດ້ານຫຼັງ", hint: "ຫຼັງສິນຄ້າ" },
  { kind: "side", label: "ດ້ານຂ້າງ", hint: "ຊ້າຍ / ຂວາ" },
  { kind: "detail", label: "ລາຍລະອຽດ", hint: "ໃກ້ໆ ໃຫ້ເຫັນລາຍລະອຽດ" },
  { kind: "package", label: "ເຕັມໂຕ / ໃນກ່ອງ", hint: "ຕິດຕັ້ງ ຫຼື ໃນກ່ອງ" },
];

const PLATE_NAVY = "#1e3a5f";
const PLATE_BLUE = "#3d6fa5";
const PLATE_BG = "#e6f0fb";

/** ຮູບປະກອບ flat ສະໄຕລ໌ scan-frame (ຄ້າຍ KYC) — ອະໄຫຼ່ຈາກແຕ່ລະມຸມ */
function SkuAnglePlate({ kind }: { kind: SkuAngleKind }) {
  return (
    <svg viewBox="0 0 64 64" className="h-full w-full" role="img" aria-hidden>
      <rect x="0" y="0" width="64" height="64" rx="10" fill={PLATE_BG} />
      {/* scan-frame corner brackets */}
      <g
        fill="none"
        stroke={PLATE_NAVY}
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M18 26 L18 20 Q18 18 20 18 L26 18" />
        <path d="M38 18 L44 18 Q46 18 46 20 L46 26" />
        <path d="M18 38 L18 44 Q18 46 20 46 L26 46" />
        <path d="M46 38 L46 44 Q46 46 44 46 L38 46" />
      </g>
      <SkuAnglePlateShape kind={kind} />
    </svg>
  );
}

function SkuAnglePlateShape({ kind }: { kind: SkuAngleKind }) {
  switch (kind) {
    case "front":
      return (
        <g>
          <circle cx="32" cy="32" r="11" fill={PLATE_BLUE} />
          <circle cx="32" cy="32" r="4.5" fill={PLATE_BG} />
          <circle cx="32" cy="32" r="2" fill={PLATE_NAVY} />
        </g>
      );
    case "back":
      return (
        <g>
          <circle cx="32" cy="32" r="11" fill={PLATE_BLUE} />
          <circle cx="32" cy="32" r="4.5" fill={PLATE_BG} />
          <g stroke={PLATE_BG} strokeWidth="1.6">
            <line x1="32" y1="22" x2="32" y2="27.5" />
            <line x1="32" y1="36.5" x2="32" y2="42" />
            <line x1="22" y1="32" x2="27.5" y2="32" />
            <line x1="36.5" y1="32" x2="42" y2="32" />
          </g>
        </g>
      );
    case "side":
      return (
        <g>
          <rect x="26" y="21" width="12" height="22" rx="3" fill={PLATE_BLUE} />
          <circle cx="32" cy="32" r="2.5" fill={PLATE_BG} />
        </g>
      );
    case "detail":
      return (
        <g>
          <circle cx="29" cy="29" r="9" fill={PLATE_BLUE} />
          <circle cx="29" cy="29" r="3.5" fill={PLATE_BG} />
          <circle
            cx="35"
            cy="35"
            r="6"
            fill="none"
            stroke={PLATE_NAVY}
            strokeWidth="2.5"
          />
          <line
            x1="39.5"
            y1="39.5"
            x2="44"
            y2="44"
            stroke={PLATE_NAVY}
            strokeWidth="3"
            strokeLinecap="round"
          />
        </g>
      );
    case "package":
      return (
        <g>
          <rect x="21" y="24" width="22" height="16" rx="2" fill={PLATE_BLUE} />
          <line
            x1="21"
            y1="30"
            x2="43"
            y2="30"
            stroke={PLATE_BG}
            strokeWidth="2"
          />
          <circle cx="32" cy="35" r="3" fill={PLATE_BG} />
        </g>
      );
  }
}

type ShopSkuMediaSectionProps = {
  shopId: string;
  /** ใช้เรียก API (resolve catalog_sku ฝั่ง server) */
  shopProductSkuId: string;
  /** ใช้เป็น key prefix ตอน upload ให้ตรงกับ Admin */
  catalogSkuId: string;
  canManage?: boolean;
};

function reorderArray<T>(arr: T[], fromIndex: number, toIndex: number): T[] {
  const out = [...arr];
  const [item] = out.splice(fromIndex, 1);
  if (item == null) return arr;
  out.splice(toIndex, 0, item);
  return out;
}

export function ShopSkuMediaSection({
  shopId,
  shopProductSkuId,
  catalogSkuId,
  canManage = false,
}: ShopSkuMediaSectionProps) {
  const mediaQuery = useShopSkuMediaQuery(shopId, shopProductSkuId);
  const addMedia = useAddShopSkuMedia(shopId, shopProductSkuId);
  const deleteMedia = useDeleteShopSkuMedia(shopId, shopProductSkuId);
  const reorderMedia = useReorderShopSkuMedia(shopId, shopProductSkuId);

  const media: ShopSkuMediaItem[] = mediaQuery.data ?? [];
  const atLimit = media.length >= MAX_SKU_IMAGES;

  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dropTargetIndex, setDropTargetIndex] = useState<number | null>(null);

  const handleDragStart = useCallback(
    (e: React.DragEvent, id: string) => {
      if (!canManage) return;
      setDraggedId(id);
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", id);
    },
    [canManage],
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent, index: number) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      if (canManage && draggedId != null) setDropTargetIndex(index);
    },
    [canManage, draggedId],
  );

  const handleDragLeave = useCallback(() => {
    setDropTargetIndex(null);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent, toIndex: number) => {
      e.preventDefault();
      setDropTargetIndex(null);
      setDraggedId(null);
      if (!canManage || draggedId == null) return;
      const fromIndex = media.findIndex((m) => m.id === draggedId);
      if (fromIndex === -1 || fromIndex === toIndex) return;
      const newOrder = reorderArray(media, fromIndex, toIndex);
      reorderMedia.mutate(newOrder.map((m) => m.id));
    },
    [canManage, draggedId, media, reorderMedia],
  );

  const handleDragEnd = useCallback(() => {
    setDraggedId(null);
    setDropTargetIndex(null);
  }, []);

  return (
    <div className="rounded-xl border border-border/80 bg-card p-5 shadow-sm">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="font-semibold text-foreground text-sm">ຮູບ SKU</h3>
          <p className="text-muted-foreground text-xs">
            ຮູບທຳອິດ = thumbnail ບົນ Marketplace · ສູງສຸດ {MAX_SKU_IMAGES} ຮູບ
          </p>
        </div>
        {canManage && (
          <PresignedImageUploadTrigger
            keyPrefix={`${KEY_PREFIX}/${catalogSkuId}`}
            onSuccess={(key) => {
              addMedia.mutate(key);
            }}
            onSuccessMultiple={(keys) => {
              for (const key of keys) addMedia.mutate(key);
            }}
            variant="button"
            label="ເພີ່ມຮູບ"
            multiple
            disabled={atLimit || addMedia.isPending}
            className="shrink-0"
          />
        )}
      </div>

      {/* ຕົວຢ່າງຮູບ — ແນະນຳ 4–5 ຮູບ ຈາກຫຼາຍມຸມ */}
      <div className="mb-4 rounded-lg border border-border/60 border-dashed bg-muted/10 p-3">
        <p className="mb-2 flex items-center gap-1.5 text-muted-foreground text-xs">
          <CameraIcon className="size-3.5 shrink-0" aria-hidden />
          ຕົວຢ່າງຮູບຖ່າຍ — ແນະນຳ 4–5 ຮູບ ຈາກຫຼາຍມຸມ
        </p>
        <ul className="grid list-none grid-cols-3 gap-2 p-0 sm:grid-cols-5">
          {SKU_PHOTO_GUIDE.map((g, i) => (
            <li
              key={g.label}
              className="flex flex-col items-center gap-1 rounded-md border border-border/50 bg-background/60 p-2 text-center"
            >
              <div className="relative aspect-square w-full overflow-hidden rounded">
                <SkuAnglePlate kind={g.kind} />
                <span className="absolute top-1 left-1 rounded bg-[#1e3a5f] px-1 font-semibold text-[10px] text-white tabular-nums">
                  {i + 1}
                </span>
              </div>
              <span className="font-medium text-[11px] text-foreground leading-tight">
                {g.label}
              </span>
              <span className="text-[10px] text-muted-foreground leading-tight">
                {g.hint}
              </span>
            </li>
          ))}
        </ul>
      </div>

      {mediaQuery.isLoading ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="aspect-square animate-pulse rounded-lg bg-muted"
              aria-hidden
            />
          ))}
        </div>
      ) : media.length === 0 ? (
        <p className="rounded-lg border border-muted-foreground/30 border-dashed bg-muted/10 py-8 text-center text-muted-foreground text-sm">
          ຍັງບໍ່ມີຮູບ SKU — ກົດເພີ່ມຮູບເພື່ອອັບໂຫຼດ
        </p>
      ) : (
        <ul className="grid list-none grid-cols-2 gap-3 p-0 sm:grid-cols-3 md:grid-cols-4">
          {media.map((m, index) => (
            <li
              key={m.id}
              aria-grabbed={canManage && draggedId === m.id}
              draggable={canManage}
              onDragStart={(e) => handleDragStart(e, m.id)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, index)}
              onDragEnd={handleDragEnd}
              className={`group relative aspect-square overflow-hidden rounded-lg border bg-muted/20 ${
                draggedId === m.id
                  ? "opacity-60"
                  : dropTargetIndex === index
                    ? "ring-2 ring-primary ring-offset-2"
                    : "border-border/60"
              } ${canManage ? "cursor-grab active:cursor-grabbing" : ""}`}
            >
              <AppImage
                src={m.url}
                alt="ຮູບ SKU"
                className="size-full"
                fit="cover"
              />
              {index === 0 && (
                <span className="absolute top-1 left-1 rounded bg-primary px-2 py-0.5 font-medium text-primary-foreground text-xs">
                  Thumbnail
                </span>
              )}
              {canManage && (
                <div className="absolute top-1 right-1 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                  <span
                    className="flex size-8 cursor-grab items-center justify-center rounded bg-black/50 text-white active:cursor-grabbing"
                    aria-hidden
                  >
                    <GripVerticalIcon className="size-4" />
                  </span>
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon"
                    className="size-8"
                    disabled={deleteMedia.isPending}
                    onClick={() => deleteMedia.mutate(m.id)}
                    aria-label="ລຶບຮູບ"
                  >
                    <Trash2Icon className="size-4" aria-hidden />
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
